<template>
  <div
    class="Indicator"
    :class="{
      'is-loading': !isLoaded,
      'is-fullscreen': isFullscreen,
    }"
  >
    <atlas-unsupported v-if="!isSupported" />

    <atlas-loader v-else-if="!isLoaded" />

    <atlas-error-page
      v-else-if="error"
      :title="error"
      :status="status"
      :message="errorMessage"
    />

    <atlas-widget-main
      v-if="isSupported && !isLoading && !error"
      v-show="isLoaded"
      :breakdown-id="breakdownId"
      :args="args"
      :geo-code-a="geo"
      :geo-code-b="geoCompareCode"
      :geo-type="geoType"
      :geo-complexity="geoComplexity"
      :modifier="displayMode"
      :data-version="dataVersion"
      :data-version-override="dataVersionOverride"
      :tenant="tenant"
      :disabledDownloadImage="disabledDownloadImage"
      :disabledDownloadPowerpoint="disabledDownloadPowerpoint"
      :disabledDownloadXlsx="disabledDownloadXlsx"
      :display-group-revision="displayRevision"
      @load="onLoad"
      @breakdown:updated="handleBreakdownUpdate"
      @setDefaultGeoCompareState="
        (props) => this.$emit('set-default-geo-compare-state', props)
      "
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapMutations, mapActions, mapState, mapGetters } from 'vuex';

import { normalizeError } from '@datalas/common';

import { DISPLAY_MODES, DISPLAY_OPTIONS } from '../../conf/display-options';

import AtlasWidgetMain from './main.vue';
import AtlasUnsupported from './unsupported.vue';

import { SKIN_KEYS } from '../../constants';

export default defineComponent({
  name: 'atlas-indicator',

  inject: ['widgetContext'],

  emits: ['set-default-geo-compare-state'],

  components: {
    AtlasUnsupported,
    AtlasWidgetMain,
  },

  props: {
    indicatorId: {
      type: String,
      required: true,
    },

    breakdownId: {
      type: String,
    },

    args: {
      type: Object,
      default: () => ({}),
    },

    geoCode: {
      type: String,
    },

    geoCompareCode: {
      type: String,
    },

    geoType: {
      type: String,
    },

    geoPersistence: {
      type: Boolean,
      default: true,
    },

    geoComplexity: {
      type: String,
    },

    displayMode: {
      type: String,
      default: 'app',
    },
    tenant: {
      type: String,
    },

    disabledDownloadImage: { type: Boolean },
    disabledDownloadPowerpoint: { type: Boolean },
    disabledDownloadXlsx: { type: Boolean },

    // Display options
    ...DISPLAY_OPTIONS.reduce((acc, option) => ({ ...acc, [option]: {} }), {}),
  },

  data: () => ({
    isLoading: true, // The indicator (config) is still loading
    isLoaded: false, // Both the indicator AND its contents have loaded

    status: null,
    error: null,
    errorMessage: null,
  }),

  computed: {
    ...mapState('ui', ['isFullscreen', 'isSupported']),

    ...mapGetters('indicator', [
      'indicatorInitialized',
      'indicatorConfig',
      'defaultIndicator',
      'activeGeoCode', // formerly as `persistentGeo` in local storage
    ]),

    ...mapGetters('indicator', {
      indicatorDisplayRev: 'displayRev',
      currentIndicatorId: 'indicatorId',
    }),

    query() {
      return (this.$route || {}).query || {};
    },

    geo() {
      return (
        this.geoCode || (this.geoPersistence && this.activeGeoCode) || undefined
      );
    },

    skin() {
      return this.widgetContext.value.skin || this.indicatorConfig?.skin;
    },

    displayOptions() {
      const skin = this.skin === SKIN_KEYS.DATA_SUMMARY ? 'figure' : null;

      const mode =
        DISPLAY_MODES[skin] ||
        DISPLAY_MODES[this.displayMode] ||
        DISPLAY_MODES.app;

      return DISPLAY_OPTIONS.reduce((acc, option) => {
        if (this[option] !== undefined) acc[option] = this[option];
        else if (mode[option] !== undefined) acc[option] = mode[option];
        return acc;
      }, {});
    },

    displayRevision() {
      // display group version
      return this.widgetContext.value.displayGroupRevision;
    },
    dataVersion() {
      // dataset version
      return this.widgetContext.value.datasetRevision;
    },
    // dataset version override
    // in case you upload an update as a separate dataset instead of a new revision
    // and then need to test the transition from the old dataset to new dataset
    dataVersionOverride() {
      return this.widgetContext.value.datasetRevisionOverride;
    },

    indicatorInitializedForDisplay() {
      return (
        !(
          this.indicatorInitialized &&
          this.indicatorDisplayRev === this.displayRevision
        ) ||
        this.currentIndicatorId !== (this.indicatorId ?? this.defaultIndicator)
      );
    },
  },

  watch: {
    indicatorId: {
      immediate: true,
      handler() {
        this.setIndicator();
      },
    },

    isLoading: {
      immediate: true,
      handler(isLoading) {
        if (isLoading) this.setDocumentTitle({ title: 'Loading…' });
      },
    },

    error(error) {
      if (error) this.setDocumentTitle({ title: error });
    },

    displayMode: {
      immediate: true,
      handler(mode) {
        this.$store.commit('display/setMode', { mode });
      },
    },

    displayOptions: {
      immediate: true,
      handler(options) {
        this.$store.commit('display/setOptions', options);
      },
    },

    displayRevision: {
      immediate: false,
      handler(value) {
        this.handleDisplayRevChange(value);
      },
    },
  },

  methods: {
    ...mapMutations('ui', ['setDocumentTitle']),

    ...mapActions('indicator', [
      'fetchIndicator',
      'fetchIndicatorGeoSelectorOptions',
      'updateCompareGeoCode',
    ]),

    async setIndicator() {
      const indicator = this.indicatorId ?? this.defaultIndicator;

      if (!this.isSupported || !indicator) {
        this.isLoading = false;
        this.isLoaded = true;
        return;
      }

      this.status = null;
      this.error = null;
      this.errorMessage = null;
      this.isLoading = true;
      this.isLoaded = false;

      try {
        if (this.indicatorInitializedForDisplay) {
          await this.fetchIndicator({
            id: indicator,
            displayRev: this.displayRevision,
            geoCode: this.geo,
          });
        }

        // fetch geo selector options to compare
        if (this.geoCompareCode) {
          await this.fetchIndicatorGeoSelectorOptions({
            id: indicator,
            displayRev: this.displayRevision,
            geoCode: this.geoCompareCode,
          });
        }

        this.isLoading = false;
        this.setDocumentTitle({ title: this.indicatorConfig.title });
      } catch (error) {
        this.isLoading = false;
        this.isLoaded = true;

        const { status, text, message } = normalizeError(error);
        this.status = status;
        this.error = text;
        this.errorMessage = message;
      }
    },

    handleDisplayRevChange() {
      this.setIndicator();
    },

    onLoad() {
      this.isLoaded = true;
    },

    handleBreakdownUpdate() {
      // reset geo compare
      this.updateCompareGeoCode('');
    },
  },

  beforeUnmount() {
    this.$store.dispatch('indicator/resetIndicator');
  },
});
</script>

<style lang="scss">
.Indicator {
  position: relative;
  box-sizing: border-box;

  &.is-loading {
    min-height: 400px;
  }

  // Safari doesn't handle position:fixed inside position:relative correctly,
  // changing the wrapper from relative to static causes it to fix itself though
  &.is-fullscreen {
    position: static;
  }

  // These global styles have to be scoped to the indicator to avoid leaking
  // into the rest of the page in the lib build.
  *,
  *::before,
  *::after {
    box-sizing: inherit;
  }

  a {
    color: color(primary);
  }
}
</style>
